import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store';
import axios from 'axios';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OIDC Callback Handler for Amos ID
 * Handles the redirect from Amos ID after authentication
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuthStore();
  
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Verificando autenticación...');
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      // Get params from URL
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      // Check for errors from Amos ID
      if (error) {
        setStatus('error');
        setMessage('Error de autenticación');
        setErrorDetails(errorDescription || error);
        return;
      }

      // Validate required params
      if (!code) {
        setStatus('error');
        setMessage('Respuesta inválida');
        setErrorDetails('No se recibió código de autorización');
        return;
      }

      // Get stored state and code_verifier from sessionStorage
      const storedState = sessionStorage.getItem('amos_id_state');
      const codeVerifier = sessionStorage.getItem('amos_id_code_verifier');
      const returnUrl = sessionStorage.getItem('amos_id_return_url') || '/';

      // Validate state (CSRF protection)
      if (state !== storedState) {
        setStatus('error');
        setMessage('Error de seguridad');
        setErrorDetails('El estado de la sesión no coincide. Intenta iniciar sesión nuevamente.');
        return;
      }

      if (!codeVerifier) {
        setStatus('error');
        setMessage('Error de sesión');
        setErrorDetails('No se encontró el verificador PKCE. Intenta iniciar sesión nuevamente.');
        return;
      }

      try {
        setMessage('Intercambiando código por token...');

        // Exchange code for tokens via backend
        const response = await axios.post(`${API_URL}/api/auth/amos-id/callback`, {
          code,
          code_verifier: codeVerifier,
          redirect_uri: `${window.location.origin}/auth/callback`
        });

        if (response.data.token && response.data.user) {
          // Clear stored PKCE data
          sessionStorage.removeItem('amos_id_state');
          sessionStorage.removeItem('amos_id_code_verifier');
          sessionStorage.removeItem('amos_id_return_url');

          // Set auth state
          setToken(response.data.token);
          setUser(response.data.user);

          setStatus('success');
          setMessage('¡Autenticación exitosa!');

          // Redirect after short delay
          setTimeout(() => {
            navigate(returnUrl, { replace: true });
          }, 1500);
        } else {
          throw new Error('Respuesta inválida del servidor');
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage('Error al completar autenticación');
        setErrorDetails(
          err.response?.data?.detail || 
          err.message || 
          'Error desconocido. Intenta nuevamente.'
        );
      }
    };

    handleCallback();
  }, [searchParams, navigate, setUser, setToken]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4"
      data-testid="auth-callback-page"
    >
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Clipper</h1>
          <p className="text-slate-400 text-sm">Sports Replay App</p>
        </div>

        {/* Status Icon */}
        <div className="mb-6">
          {status === 'processing' && (
            <Loader2 className="w-16 h-16 mx-auto text-emerald-500 animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-500" />
          )}
          {status === 'error' && (
            <XCircle className="w-16 h-16 mx-auto text-red-500" />
          )}
        </div>

        {/* Message */}
        <h2 className="text-xl font-semibold text-white mb-2" data-testid="auth-callback-status">{message}</h2>
        
        {/* Error Details */}
        {errorDetails && (
          <p className="text-red-400 text-sm mb-6" data-testid="auth-callback-error">{errorDetails}</p>
        )}

        {/* Success redirect notice */}
        {status === 'success' && (
          <p className="text-slate-400 text-sm">Redirigiendo...</p>
        )}

        {/* Error actions */}
        {status === 'error' && (
          <div className="mt-6 space-y-3">
            <button
              onClick={() => navigate('/auth')}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-4 rounded-xl transition-colors"
              data-testid="auth-callback-retry-button"
            >
              Volver a iniciar sesión
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-3 px-4 rounded-xl transition-colors"
              data-testid="auth-callback-home-button"
            >
              Ir al inicio
            </button>
          </div>
        )}

        {/* Amos ID branding */}
        <div className="mt-8 pt-6 border-t border-slate-700">
          <p className="text-slate-500 text-xs">
            Autenticación segura con Amos ID
          </p>
        </div>
      </div>
    </div>
  );
}
